import { Copy, ShieldCheck, Smartphone } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Banner, Button } from '@/components/ui'
import { BLOCK_AREA } from '@/lib/analytics'
import { errorMessage } from '@/lib/api'
import { t } from '@/lib/i18n'
import { OtpInput } from '../components/OtpInput'
import { copyText, groupSecret, useMfaConfirm, useMfaEnroll, type MfaEnrollment } from './api'
import { RecoveryCodes } from './RecoveryCodes'

type Step = 'intro' | 'scan' | 'codes'

export interface MfaSetupProps {
  /** Called once the recovery codes were saved: this session is verified. */
  onDone: () => void
  /** "Not now" (the full screen gate) or "Cancel" (settings). Hidden when not given. */
  onCancel?: () => void
  cancelLabel?: string
  /** Start at the scan step at once (settings, where the button already said "Turn on"). */
  autoStart?: boolean
}

/** G-03 enrolment: scan the QR code (or type the key), confirm a code, save the 10 recovery codes. */
export function MfaSetup({ onDone, onCancel, cancelLabel, autoStart }: MfaSetupProps) {
  const [step, setStep] = useState<Step>('intro')
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null)
  const [codes, setCodes] = useState<string[]>([])
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const enroll = useMfaEnroll()
  const confirm = useMfaConfirm()
  const started = useRef(false)

  const start = async () => {
    setError(null)
    try {
      setEnrollment(await enroll.mutateAsync())
      setStep('scan')
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  useEffect(() => {
    if (autoStart && !started.current) {
      started.current = true
      void start()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on open
  }, [autoStart])

  const submit = async (e?: FormEvent, value = code) => {
    e?.preventDefault()
    if (confirm.isPending || value.length !== 6) return
    setError(null)
    try {
      const out = await confirm.mutateAsync(value)
      setCodes(out.recoveryCodes)
      setStep('codes')
    } catch (err) {
      setError(errorMessage(err))
      setCode('')
    }
  }

  const cancel = onCancel ? (
    <Button type="button" variant="ghost" onClick={onCancel}>
      {cancelLabel ?? t('Cancel')}
    </Button>
  ) : null

  if (step === 'intro') {
    return (
      <div className="space-y-4" data-testid="mfa-setup-intro">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className="flex gap-3 rounded-card border border-line bg-surface-2 p-4">
          <Smartphone className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden="true" />
          <p className="text-body text-text">
            {t('You need an Authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or 1Password.')}
          </p>
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {cancel}
          <Button type="button" loading={enroll.isPending} onClick={() => void start()}>
            {t('Start setup')}
          </Button>
        </div>
      </div>
    )
  }

  if (step === 'scan' && enrollment) {
    return (
      <form onSubmit={(e) => void submit(e)} noValidate className="space-y-5" data-testid="mfa-setup-scan">
        <ol className="space-y-5">
          <li className="space-y-3">
            <p className="text-body font-semibold text-text">{t('1. Scan this QR code with your Authenticator app')}</p>
            {/* the QR code, the key and the otpauth: link (it holds the key and the address): an empty box in session
                replay and no click events (docs/analytics.md section 7) */}
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start" data-testid="mfa-enrollment" {...BLOCK_AREA}>
              <img
                src={enrollment.qrSvg}
                alt={t('QR code to scan with your Authenticator app')}
                width={176}
                height={176}
                className="size-44 shrink-0 rounded-control border border-line bg-white p-2"
              />
              <div className="min-w-0 space-y-2 text-meta text-muted">
                <p>{t('Cannot scan it? Add an account in the app and type this key:')}</p>
                <p className="break-all rounded-control bg-surface-2 px-3 py-2 font-mono text-body text-text" data-testid="mfa-secret" {...BLOCK_AREA}>
                  {groupSecret(enrollment.secret)}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="secondary" leftIcon={<Copy />} onClick={() => void copyText(enrollment.secret, t('Key copied'))}>
                    {t('Copy key')}
                  </Button>
                  <Button asChild size="sm" variant="ghost">
                    <a href={enrollment.otpauthUri}>{t('Open in my Authenticator app')}</a>
                  </Button>
                </div>
              </div>
            </div>
          </li>
          <li className="space-y-3">
            <p className="text-body font-semibold text-text">{t('2. Enter the 6 digit code the app shows')}</p>
            {error ? <Banner tone="danger">{error}</Banner> : null}
            <OtpInput value={code} onChange={setCode} onComplete={(v) => void submit(undefined, v)} invalid={!!error} label={t('Authenticator app code')} />
          </li>
        </ol>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {cancel}
          <Button type="submit" loading={confirm.isPending} disabled={code.length !== 6}>
            {t('Confirm')}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <div className="space-y-4">
      <Banner tone="info" icon={<ShieldCheck />}>{t('Two factor authentication is on.')}</Banner>
      <RecoveryCodes codes={codes} account={enrollment?.account ?? ''} onDone={onDone} />
    </div>
  )
}
